import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { toast, Bounce } from "react-toastify";
import {
    NotificationTabId,
    applyEoyChangeToEvents,
    areAnyEventsOff,
    buildNotificationConfigUpdateBody,
    canSelectNotificationsTab,
    canSubmitNotificationConfig,
    findPlanInGrids,
    getFilteredEvents,
    getSelectedCommsLibrary,
    getSelectedLanguage,
    hasNotificationConfigChanged,
    hydrateNotificationFormState,
    isPartnershipPlan,
    isRbpYes,
    loadAllPlansGrids,
    mapPlanRowToAutoPopulatedFields,
    parsePlanRouteSearchParams,
    updatePlanNotificationInGridsData,
    validateVariablesForm,
} from "../../../../../utils/planManager/planConfigurationUtils.js";
import {
    fetchPlanManagerClientConfig,
    fetchPlanManagerDefaultNotificationConfig,
    fetchPlanManagerHealthEdgeConfig,
    fetchPlanManagerHealthEdgePlansBatch,
    fetchPlanManagerPlansBatch,
    fetchPlanManagerUpdateNotificationConfig,
} from "../../../../../utils/apiUtils.js";
import { useSelectedPlanStore } from "../../../../../store/selectedPlanStore.js";
import { useAuthStore } from "../../../../../store/authStore.js";
import {
    PLANS_LIST_ROUTE,
    PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    PLAN_MANAGER_QUERY_KEYS,
    PlanPlatformTab,
} from "../../../common/constants";


/**
 * Tab options for the Variables / Notifications segmented control.
 * @readonly
 */
const NOTIFICATION_TAB_OPTIONS = Object.freeze([
    {
        value: NotificationTabId.VARIABLES,
        label: "Variables",
        id: "tab-variables",
        controls: "panel-variables",
    },
    {
        value: NotificationTabId.NOTIFICATIONS,
        label: "Notifications",
        id: "tab-notifications",
        controls: "panel-notifications",
    },
]);


/**
 * Toast messages for notification submit feedback.
 * @readonly
 */
export const NOTIFICATION_TOAST_MESSAGES = Object.freeze({
    SUBMIT: "Notification saved successfully!",
    SUBMIT_ERROR: "Failed to save notification configuration. Please try again.",
});

/**
 * Shared `react-toastify` options for notification action toasts.
 * @type {import("react-toastify").ToastOptions}
 */
const NOTIFICATION_TOAST_OPTIONS = Object.freeze({
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    transition: Bounce,
});


/**
 * Copy for the unsaved-changes warning modal.
 * @readonly
 */
export const UNSAVED_CHANGES_MODAL_COPY = Object.freeze({
    TITLE: "Submit your changes?",
    BODY: "Go to the notifications tab to submit your changes. If you do not click on the submit button on the notifications tab, your changes will not be saved.",
    STAY: "Yes, I want to submit my changes",
    DISCARD: "No, I don't want to submit my changes",
});

const NOTIFICATION_LOAD_ERROR_MESSAGE =
    "Plan manager data is currently unavailable. Please try again later.";

/**
 * Marks a single option selected within an option list (radio semantics).
 * @param {ReadonlyArray<object>} options
 * @param {string} selectedTitle
 * @returns {Array<object>}
 */
const selectSingleOptionByTitle = (options, selectedTitle) =>
    options.map((option) => ({
        ...option,
        isSelected: option.title === selectedTitle,
    }));

/**
 * Ephemeral form state and handlers for the plan notification setup screen.
 * @returns {object} Values and callbacks for the presentational view.
 */
export function useNotification() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const hydrationKeyRef = useRef(null);
    const originalFormRef = useRef({});

    const storePlanRow = useSelectedPlanStore((state) => state.planRow);
    const storeClientId = useSelectedPlanStore((state) => state.clientId);
    const storePlanId = useSelectedPlanStore((state) => state.planId);
    const storePlatformTab = useSelectedPlanStore((state) => state.platformTab);
    const setSelectedPlan = useSelectedPlanStore((state) => state.setSelectedPlan);
    const authEmail = useAuthStore((state) => state.email);

    const parsedParams = useMemo(
        () => parsePlanRouteSearchParams(searchParams),
        [searchParams],
    );

    const clientId = storeClientId || parsedParams.clientId;
    const planId = storePlanId || parsedParams.planId;
    const platformTab =
        storePlatformTab ||
        parsedParams.platformTab ||
        (planId?.startsWith("HRP_")
            ? PlanPlatformTab.HRP
            : PlanPlatformTab.JAVELINA);

    useEffect(() => {
        if (!clientId || !planId) {
            navigate(PLANS_LIST_ROUTE, { replace: true });
        }
    }, [clientId, planId, navigate]);

    const needsPlansLookup = Boolean(clientId && planId && !storePlanRow);

    const plansAllQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
        queryFn: () =>
            loadAllPlansGrids({
                fetchJavelinaConfigPage: fetchPlanManagerClientConfig,
                fetchJavelinaBatch: fetchPlanManagerPlansBatch,
                fetchHealthEdgeConfigPage: fetchPlanManagerHealthEdgeConfig,
                fetchHealthEdgeBatch: fetchPlanManagerHealthEdgePlansBatch,
            }),
        enabled: needsPlansLookup,
        ...PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    });

    const planRow = useMemo(() => {
        if (storePlanRow) {
            return storePlanRow;
        }
        if (!plansAllQuery.data) {
            return null;
        }
        return findPlanInGrids({
            jvPlans: plansAllQuery.data.jvPlans,
            hrpPlans: plansAllQuery.data.hrpPlans,
            clientId,
            planId,
            platformTab,
        });
    }, [storePlanRow, plansAllQuery.data, clientId, planId, platformTab]);

    const [form, setForm] = useState({});
    const [isFormHydrated, setIsFormHydrated] = useState(false);
    const [selectedTabId, setSelectedTabId] = useState(
        NotificationTabId.VARIABLES,
    );
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [shouldShowValidationErrors, setShouldShowValidationErrors] =
        useState(false);

    const defaultNotificationConfigQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.defaultNotificationConfig,
        queryFn: fetchPlanManagerDefaultNotificationConfig,
        ...PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    });

    useEffect(() => {
        hydrationKeyRef.current = null;
        setIsFormHydrated(false);
        setForm({});
        setSelectedTabId(NotificationTabId.VARIABLES);
        setIsWarningModalOpen(false);
        setShouldShowValidationErrors(false);
    }, [clientId, planId]);

    useEffect(() => {
        if (!clientId || !planId) {
            return;
        }

        if (
            defaultNotificationConfigQuery.isLoading ||
            defaultNotificationConfigQuery.isError ||
            (needsPlansLookup && plansAllQuery.isLoading) ||
            (needsPlansLookup && plansAllQuery.isError)
        ) {
            return;
        }

        if (needsPlansLookup && !planRow) {
            return;
        }

        const configSignature = JSON.stringify(planRow?.notificationConfig ?? null);
        const hydrationKey = `${clientId}#${planId}#${configSignature}`;

        if (hydrationKeyRef.current === hydrationKey) {
            return;
        }

        const { formData } = hydrateNotificationFormState({
            planRow,
            defaultNotificationConfig: defaultNotificationConfigQuery.data,
        });

        const hydratedForm = JSON.parse(JSON.stringify(formData));

        setForm(hydratedForm);
        originalFormRef.current = hydratedForm;
        hydrationKeyRef.current = hydrationKey;
        setIsFormHydrated(true);
    }, [
        clientId,
        planId,
        planRow,
        needsPlansLookup,
        plansAllQuery.isLoading,
        plansAllQuery.isError,
        defaultNotificationConfigQuery.data,
        defaultNotificationConfigQuery.isError,
        defaultNotificationConfigQuery.isLoading,
    ]);

    const validationErrors = useMemo(() => {
        if (!shouldShowValidationErrors) {
            return {};
        }

        return validateVariablesForm(form);
    }, [form, shouldShowValidationErrors]);
    const selectedCommsLibrary = useMemo(
        () => getSelectedCommsLibrary(form.commsLibraryOptions),
        [form.commsLibraryOptions],
    );
    const isPartnership = isPartnershipPlan(selectedCommsLibrary);
    const isPcpCopayMissing = !form.pcpCopay?.some((option) => option.isSelected);
    const isRbpSelectedYes = isRbpYes(form.rbpOptions);
    const networkNames = useMemo(
        () => selectedCommsLibrary?.networkNames ?? [],
        [selectedCommsLibrary],
    );
    const selectedLanguage = getSelectedLanguage(form.languages);
    const events = useMemo(
        () => getFilteredEvents(form.commsLibraryOptions),
        [form.commsLibraryOptions],
    );
    const hasConfigChange = hasNotificationConfigChanged(
        form,
        originalFormRef.current,
    );
    const autoPopulated = useMemo(
        () =>
            mapPlanRowToAutoPopulatedFields(planRow, form.administratorName),
        [planRow, form.administratorName],
    );

    const handleFieldChange = (field, value) => {
        setForm((previous) => ({ ...previous, [field]: value }));
    };

    const handleSelectOption = (listKey, title) => {
        setForm((previous) => {
            const nextForm = {
                ...previous,
                [listKey]: selectSingleOptionByTitle(previous[listKey], title),
            };

            if (listKey === "commsLibraryOptions") {
                nextForm.commsLibraryOptions = previous.commsLibraryOptions.map(
                    (library) => {
                        const isSelected = library.title === title;
                        const nextLibrary = {
                            ...library,
                            isSelected,
                        };

                        if (isSelected && nextLibrary.networkNames) {
                            nextLibrary.networkNames =
                                nextLibrary.networkNames.map((network) => ({
                                    ...network,
                                    isSelected: false,
                                    value:
                                        network.title === "Other"
                                            ? ""
                                            : network.value,
                                }));
                        }

                        return nextLibrary;
                    },
                );
            }

            return nextForm;
        });
    };

    const handleNetworkSelect = (networkTitle) => {
        setForm((previous) => ({
            ...previous,
            commsLibraryOptions: previous.commsLibraryOptions.map((library) => {
                if (!library.isSelected || !library.networkNames) {
                    return library;
                }

                return {
                    ...library,
                    networkNames: library.networkNames.map((network) => ({
                        ...network,
                        isSelected: network.title === networkTitle,
                        value:
                            network.title === "Other" && networkTitle !== "Other"
                                ? ""
                                : network.value,
                    })),
                };
            }),
        }));
    };

    const handleNetworkOtherChange = (value) => {
        setForm((previous) => ({
            ...previous,
            commsLibraryOptions: previous.commsLibraryOptions.map((library) => {
                if (!library.isSelected || !library.networkNames) {
                    return library;
                }

                return {
                    ...library,
                    networkNames: library.networkNames.map((network) =>
                        network.title === "Other"
                            ? { ...network, value: value.trim() }
                            : network,
                    ),
                };
            }),
        }));
    };

    const handlePcpCopaySelect = (title) => {
        setForm((previous) => ({
            ...previous,
            pcpCopay: selectSingleOptionByTitle(previous.pcpCopay, title),
        }));
    };

    const handlePcpCopayOtherChange = (value) => {
        setForm((previous) => ({
            ...previous,
            pcpCopay: previous.pcpCopay.map((option) =>
                option.title === "Other"
                    ? { ...option, value: value.trim() }
                    : option,
            ),
        }));
    };

    const handleReferralSelect = (referralTitle, optionLabel) => {
        setForm((previous) => ({
            ...previous,
            referralOptions: previous.referralOptions.map((referralGroup) => {
                if (referralGroup.title !== referralTitle) {
                    return referralGroup;
                }

                return {
                    ...referralGroup,
                    options: referralGroup.options.map((option) => {
                        const isSelected = option.label.title === optionLabel;
                        const nextOption = { ...option, isSelected };

                        if (isSelected && !option.isCustomInput) {
                            nextOption.value = option.value || optionLabel;
                        }

                        if (isSelected && option.label.title === "N/A") {
                            nextOption.value = "N/A";
                        }

                        if (!isSelected && option.isCustomInput) {
                            nextOption.value = "";
                        }

                        return nextOption;
                    }),
                };
            }),
        }));
    };

    const handleReferralCustomChange = (referralTitle, value) => {
        setForm((previous) => ({
            ...previous,
            referralOptions: previous.referralOptions.map((referralGroup) => {
                if (referralGroup.title !== referralTitle) {
                    return referralGroup;
                }

                return {
                    ...referralGroup,
                    options: referralGroup.options.map((option) =>
                        option.isSelected && option.isCustomInput
                            ? { ...option, value: value.trim() }
                            : option,
                    ),
                };
            }),
        }));
    };

    const handleRbpSelect = (title) => {
        setForm((previous) => ({
            ...previous,
            rbpOptions: selectSingleOptionByTitle(previous.rbpOptions, title),
        }));
    };

    const handleRbpLanguageSelect = (fieldKey, title) => {
        setForm((previous) => ({
            ...previous,
            [fieldKey]: selectSingleOptionByTitle(previous[fieldKey], title),
        }));
    };

    const handleRbpLanguageTextChange = (fieldKey, value) => {
        setForm((previous) => ({
            ...previous,
            [fieldKey]: previous[fieldKey].map((option) =>
                option.isSelected
                    ? { ...option, value: value.trim() }
                    : option,
            ),
        }));
    };

    const handleEventToggle = (rowKey, isSelected) => {
        const [eventTitle, eventType] = rowKey.split(":");

        setForm((previous) => ({
            ...previous,
            commsLibraryOptions: previous.commsLibraryOptions.map((library) => {
                if (!library.isSelected) {
                    return library;
                }

                return {
                    ...library,
                    events: library.events.map((event) =>
                        event.title === eventTitle && event.type === eventType
                            ? { ...event, isSelected }
                            : event,
                    ),
                };
            }),
        }));
    };

    const handleEoyChange = ({ eventTitle, eventType, isEoy }) => {
        setForm((previous) => ({
            ...previous,
            commsLibraryOptions: applyEoyChangeToEvents(
                previous.commsLibraryOptions,
                { eventTitle, eventType, isEoy },
            ),
        }));
    };

    const handleTabChange = (nextTabId) => {
        if (
            nextTabId === NotificationTabId.NOTIFICATIONS &&
            !canSelectNotificationsTab(form)
        ) {
            setShouldShowValidationErrors(true);
            return;
        }

        setSelectedTabId(nextTabId);
    };

    const handleBackClick = (event) => {
        if (!hasConfigChange) {
            return;
        }

        event.preventDefault();
        setIsWarningModalOpen(true);
    };

    const handleStayOnPage = () => {
        setIsWarningModalOpen(false);
    };

    const handleDiscardChanges = () => {
        setIsWarningModalOpen(false);
        setForm(JSON.parse(JSON.stringify(originalFormRef.current)));
        navigate(PLANS_LIST_ROUTE);
    };

    const handleSubmit = async () => {
        if (!clientId || !planId) {
            toast.error(
                NOTIFICATION_TOAST_MESSAGES.SUBMIT_ERROR,
                NOTIFICATION_TOAST_OPTIONS,
            );
            return;
        }

        try {
            const body = buildNotificationConfigUpdateBody({
                form,
                loadedNotificationConfig: planRow?.notificationConfig,
                clientId,
                planId,
                updatedBy: authEmail ?? "",
            });
            const result = await fetchPlanManagerUpdateNotificationConfig(body);

            if (result == null) {
                toast.error(
                    NOTIFICATION_TOAST_MESSAGES.SUBMIT_ERROR,
                    NOTIFICATION_TOAST_OPTIONS,
                );
                return;
            }

            queryClient.setQueryData(
                PLAN_MANAGER_QUERY_KEYS.plansAll,
                (oldGridsData) =>
                    updatePlanNotificationInGridsData({
                        gridsData: oldGridsData,
                        clientId,
                        planId,
                        notificationConfig: body.notificationConfig,
                    }),
            );

            if (planRow) {
                setSelectedPlan(
                    {
                        ...planRow,
                        hasNotificationConfig: true,
                        notificationConfig: body.notificationConfig,
                    },
                    platformTab,
                );
            }

            await queryClient.invalidateQueries({
                queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
            });
            toast.success(
                NOTIFICATION_TOAST_MESSAGES.SUBMIT,
                NOTIFICATION_TOAST_OPTIONS,
            );
            originalFormRef.current = JSON.parse(JSON.stringify(form));
            setIsWarningModalOpen(false);
            navigate(PLANS_LIST_ROUTE);
        } catch {
            toast.error(
                NOTIFICATION_TOAST_MESSAGES.SUBMIT_ERROR,
                NOTIFICATION_TOAST_OPTIONS,
            );
        }
    };

    const clientPlanDisplay = `${planRow?.clientName ?? ""} / ${planRow?.planId ?? ""}`;
    const isNotificationsTabEnabled = canSelectNotificationsTab(form);
    const showEventsOffWarning = areAnyEventsOff(form.commsLibraryOptions);
    const notificationTabs = NOTIFICATION_TAB_OPTIONS.map((option) => ({
        ...option,
        enabled:
            option.value !== NotificationTabId.NOTIFICATIONS ||
            isNotificationsTabEnabled,
    }));

    const hasSelectedPlanContext = Boolean(clientId && planId);
    const isLoading =
        !hasSelectedPlanContext ||
        defaultNotificationConfigQuery.isLoading ||
        (needsPlansLookup && plansAllQuery.isLoading) ||
        (hasSelectedPlanContext && !isFormHydrated);
    const isError =
        defaultNotificationConfigQuery.isError ||
        (needsPlansLookup && plansAllQuery.isError);
    const loadErrorMessage =
        defaultNotificationConfigQuery.error?.message ??
        plansAllQuery.error?.message ??
        NOTIFICATION_LOAD_ERROR_MESSAGE;

    return {
        form,
        selectedTabId,
        isWarningModalOpen,
        validationErrors,
        selectedCommsLibrary,
        isPartnership,
        isPcpCopayMissing,
        isRbpSelectedYes,
        networkNames,
        selectedLanguage,
        events,
        hasConfigChange,
        autoPopulated,
        clientPlanDisplay,
        notificationTabs,
        showEventsOffWarning,
        handleFieldChange,
        handleSelectOption,
        handleNetworkSelect,
        handleNetworkOtherChange,
        handlePcpCopaySelect,
        handlePcpCopayOtherChange,
        handleReferralSelect,
        handleReferralCustomChange,
        handleRbpSelect,
        handleRbpLanguageSelect,
        handleRbpLanguageTextChange,
        handleEventToggle,
        handleEoyChange,
        handleTabChange,
        handleBackClick,
        handleStayOnPage,
        handleDiscardChanges,
        handleSubmit,
        isLoading,
        isError,
        loadErrorMessage,
    };
}
